"use client";
import React, { useMemo, useRef, useState, useEffect } from "react";
import FooterBanner from "@/components/common/footerBanner";
import { showCaseComponentSwitch } from "@/components/showcase/showcase.utils";
import HomeAppComponent from "@/components/apps/homeComponent";
import { useHomeShowcaseData } from "@/hooks/useHomeShowcaseData";

// Lightweight wrapper: renders children only when near viewport
// Accepts a render function () => ReactNode so child components are NOT
// evaluated/mounted until the section is close to the viewport.
const LazySection = ({ children, immediate = false }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(immediate);

  useEffect(() => {
    if (immediate || isVisible) return;

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [immediate, isVisible]);

  if (isVisible) {
    // Support both render-function and plain children patterns
    return <>{typeof children === "function" ? children() : children}</>;
  }

  // Placeholder — matches typical section height to prevent layout shift
  return <div ref={ref} style={{ minHeight: 320 }} />;
};

const scrollHomeToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });

  const scrollElement = document.scrollingElement || document.documentElement;
  if (scrollElement) {
    scrollElement.scrollTop = 0;
    scrollElement.scrollLeft = 0;
  }
};

const HomeScreen = ({ showcaseData }) => {
  // Fetch private/personalized showcase data (continue-watch, you-may-like)
  const { continueWatch, youMayLike } = useHomeShowcaseData();

  useEffect(() => {
    let resetTimeoutId;

    const resetScroll = () => {
      scrollHomeToTop();
      resetTimeoutId = window.setTimeout(scrollHomeToTop, 100);
    };

    scrollHomeToTop();

    const frameId = window.requestAnimationFrame(scrollHomeToTop);
    const timeoutId = window.setTimeout(scrollHomeToTop, 100);
    window.addEventListener("pageshow", resetScroll);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.clearTimeout(resetTimeoutId);
      window.removeEventListener("pageshow", resetScroll);
    };
  }, []);

  const homeData = useMemo(() => {
    const results = [];

    Object.values(showcaseData).forEach((items) => {
      if (Array.isArray(items)) {
        results.push(...items);
      }
    });

    // Sort by priority
    const sorted = results.sort((a, b) => (a.priority || 0) - (b.priority || 0));

    // Inject private showcase rows at specific positions (matching App-WebTV layout)
    const finalResults = [];
    let processedCount = 0;

    for (let i = 0; i < sorted.length; i++) {
      finalResults.push(sorted[i]);
      processedCount++;

      // After 2nd item: Insert Continue Watch (if available)
      if (processedCount === 3 && continueWatch && continueWatch.length > 0) {
        finalResults.push({
          _id: "continue_watch_row",
          type: "continueWatch",
          title: "ادامه تماشا",
          items: continueWatch,
          priority: -1,
        });
      }

      // After 5th item: Insert You May Like (if available)
      if (processedCount === 6 && youMayLike && youMayLike.length > 0) {
        finalResults.push({
          _id: "you_may_like_row",
          type: "youMayLike",
          title: "شاید دوست داشته باشید",
          items: youMayLike,
          priority: -1,
        });
      }
    }



    return finalResults;
  }, [showcaseData, continueWatch, youMayLike]);

  return (
    <div className="w-screen min-h-screen">
      {homeData?.map((item, index) => (
        <React.Fragment key={`showcase-${item._id || item.id || index}`}>
          {index === 7 && <HomeAppComponent />}
          <LazySection immediate={index < 2}>
            {() => showCaseComponentSwitch(item)}
          </LazySection>
        </React.Fragment>
      ))}
      <div
        data-content-end
        className="hidden md:block h-[600px] bg-[#0A0A0A] pt-12 w-full"
      >
        <FooterBanner />
      </div>
    </div>
  );
};

export default HomeScreen;
